import type { Condition, Destination, Item, ProgressStatus } from '@easymoving/types';

export interface ItemFilters {
  room?: string;
  category?: string;
  destination?: Destination;
  progressStatus?: ProgressStatus;
  condition?: Condition;
  /** A box id, or 'none' for items not assigned to any box. */
  boxId?: string | 'none';
  fragile?: boolean;
  /** Keep only items with no real photo (icon/placeholder only). */
  missingPhoto?: boolean;
}

/** True when the item has no uploaded photo (icon or placeholder only). */
export function isMissingPhoto(it: Item): boolean {
  return (it.images ?? []).length === 0;
}

export type SortKey =
  | 'createdAt'
  | 'updatedAt'
  | 'name'
  | 'room'
  | 'category'
  | 'destination'
  | 'progressStatus';
export type SortDir = 'asc' | 'desc';

/** Fields searched by the gallery search bar (PRD §8.16) + the box name, so
 *  "where's the kettle" works post-move ("kitchen 1" finds its contents). */
function haystack(item: Item, boxNames?: ReadonlyMap<string, string>): string {
  return [
    item.name,
    item.nickname,
    item.notes,
    item.room,
    item.category,
    item.storageLocation,
    item.boxId ? boxNames?.get(item.boxId) : undefined,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

/** Every whitespace-separated term must appear (AND). Empty query → all. */
export function searchItems(
  items: Item[],
  queryText: string,
  boxNames?: ReadonlyMap<string, string>,
): Item[] {
  const terms = queryText.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return items;
  return items.filter((it) => {
    const hay = haystack(it, boxNames);
    return terms.every((term) => hay.includes(term));
  });
}

export function filterItems(items: Item[], f: ItemFilters): Item[] {
  return items.filter((it) => {
    if (f.room && it.room !== f.room) return false;
    if (f.category && it.category !== f.category) return false;
    if (f.destination && it.destination !== f.destination) return false;
    if (f.progressStatus && it.progressStatus !== f.progressStatus) return false;
    if (f.condition && it.condition !== f.condition) return false;
    if (f.fragile !== undefined && (it.fragile ?? false) !== f.fragile) return false;
    if (f.boxId === 'none' && it.boxId) return false;
    if (f.boxId && f.boxId !== 'none' && it.boxId !== f.boxId) return false;
    if (f.missingPhoto && !isMissingPhoto(it)) return false;
    return true;
  });
}

const collator = new Intl.Collator('he', { numeric: true, sensitivity: 'base' });

export function sortItems(items: Item[], key: SortKey, dir: SortDir = 'desc'): Item[] {
  const factor = dir === 'asc' ? 1 : -1;
  const copy = [...items];
  copy.sort((a, b) => {
    let cmp: number;
    switch (key) {
      case 'createdAt':
      case 'updatedAt':
        cmp = a[key] - b[key];
        break;
      default:
        cmp = collator.compare(String(a[key] ?? ''), String(b[key] ?? ''));
    }
    if (cmp === 0) cmp = a.createdAt - b.createdAt; // stable tiebreak
    return cmp * factor;
  });
  return copy;
}

/** One-shot: search → filter → sort. */
export function applyItemQuery(
  items: Item[],
  opts: {
    search?: string;
    filters?: ItemFilters;
    sortKey?: SortKey;
    sortDir?: SortDir;
    boxNames?: ReadonlyMap<string, string>;
  },
): Item[] {
  let out = items;
  if (opts.search) out = searchItems(out, opts.search, opts.boxNames);
  if (opts.filters) out = filterItems(out, opts.filters);
  out = sortItems(out, opts.sortKey ?? 'createdAt', opts.sortDir ?? 'desc');
  return out;
}
